/**
 * ecancer recently viewed history
 *
 * Usage:
 *
 *   import { initHistory, trackHistoryItem } from './history.js';
 *
 *   initHistory();
 *
 *   trackHistoryItem({
 *       id,
 *       type,
 *       locale,
 *       title,
 *       uri
 *   });
 *
 * Call initHistory() on pages where the history control is present.
 * Call trackHistoryItem() only on content pages.
 */

const STORAGE_KEY = 'ecancer.recentlyViewed.v1';
const MAX_ITEMS = 100;
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const DEFAULT_BUTTON_SELECTOR = '.history-button';
const DEFAULT_SCROLL_THRESHOLD = 100;
const DEFAULT_INTERACTION_IGNORE_SELECTOR =
    '.history-button, #ecancer-recently-viewed-panel, #page-head, header, nav';
const PANEL_ID = 'ecancer-recently-viewed-panel';
const HISTORY_PAGE_SIZE = 8;

let visibleHistoryItems = HISTORY_PAGE_SIZE;
let historyUiInitialised = false;
let panel = null;
let activeButton = null;
let buttonSelector = DEFAULT_BUTTON_SELECTOR;

function dispatchHistoryChange() {
    document.dispatchEvent(new CustomEvent('ecancer:historychange', {
        detail: {
            history: readHistory()
        }
    }));
}

function writeHistory(history) {
    try {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(history.slice(0, MAX_ITEMS))
        );

        return true;
    } catch {
        return false;
    }
}

export function readHistory() {
    let value;

    try {
        value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
        return [];
    }

    if (!Array.isArray(value)) {
        return [];
    }

    const cutoff = Date.now() - MAX_AGE_MS;

    const history = value.filter(item =>
        item &&
        item.id !== undefined &&
        item.id !== null &&
        typeof item.type === 'string' &&
        item.type.length > 0 &&
        typeof item.uri === 'string' &&
        item.uri.length > 0 &&
        typeof item.title === 'string' &&
        item.title.length > 0 &&
        Number.isFinite(item.viewedAt) &&
        item.viewedAt >= cutoff
    );

    if (history.length !== value.length) {
        writeHistory(history);
    }

    return history;
}

export function clearHistory() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // History is non-essential, so storage failures are ignored.
    }

    dispatchHistoryChange();

    if (panel && !panel.hidden) {
        renderHistory();
    }
}

export function removeHistoryItem(id, type) {
    const history = readHistory();

    const filtered = history.filter(item =>
        !(
            String(item.id) === String(id) &&
            item.type === type
        )
    );

    if (writeHistory(filtered)) {
        dispatchHistoryChange();
    }
}

function updateHistoryButtonCount() {
    const count = readHistory().length;

    for (const button of document.querySelectorAll(buttonSelector)) {
        button.dataset.historyCount = String(count);
    }
}

function normaliseItem(item) {
    if (!item || item.id === undefined || item.id === null || !item.type) {
        return null;
    }

    const title = String(item.title || document.title || '').trim();

    if (!title) {
        return null;
    }

    const uri = String(
        item.uri ||
        `${window.location.pathname}${window.location.search}`
    ).trim();

    if (!uri) {
        return null;
    }

    return {
        id: item.id,
        type: String(item.type),
        locale: item.locale
            ? String(item.locale)
            : (document.documentElement.lang || null),
        title,
        uri
    };
}

function addHistoryItem(item) {
    const normalised = normaliseItem(item);

    if (!normalised) {
        return;
    }

    const history = readHistory();

    const filtered = history.filter(existing =>
        !(
            String(existing.id) === String(normalised.id) &&
            existing.type === normalised.type
        )
    );

    filtered.unshift({
        ...normalised,
        viewedAt: Date.now()
    });

    if (writeHistory(filtered)) {
        dispatchHistoryChange();
    }
}

function interactionIsIgnored(target, ignoreSelector) {
    if (!ignoreSelector || !(target instanceof Element)) {
        return false;
    }

    return Boolean(target.closest(ignoreSelector));
}

/**
 * Store the supplied content item only after evidence of engagement.
 *
 * Evidence is one of:
 * - pointer/touch press outside ignored header/navigation areas;
 * - meaningful keyboard interaction outside ignored areas;
 * - scrolling at least scrollThreshold pixels.
 *
 * Mouse movement alone is intentionally not considered sufficient.
 *
 * Returns a cleanup function.
 */
export function trackHistoryItem(item, options = {}) {
    const normalised = normaliseItem(item);

    if (!normalised) {
        return () => {};
    }

    const scrollThreshold =
        Number.isFinite(options.scrollThreshold) && options.scrollThreshold >= 0
            ? options.scrollThreshold
            : DEFAULT_SCROLL_THRESHOLD;

    const ignoreSelector =
        options.ignoreSelector === undefined
            ? DEFAULT_INTERACTION_IGNORE_SELECTOR
            : options.ignoreSelector;

    const initialScrollY = window.scrollY;
    let recorded = false;

    const meaningfulKeys = new Set([
        'Enter',
        ' ',
        'ArrowDown',
        'ArrowUp',
        'PageDown',
        'PageUp',
        'Home',
        'End'
    ]);

    function cleanup() {
        document.removeEventListener('pointerdown', handlePointerDown);
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('scroll', handleScroll);
    }

    function record() {
        if (recorded) {
            return;
        }

        recorded = true;
        cleanup();
        addHistoryItem(normalised);
    }

    function handlePointerDown(event) {
        if (interactionIsIgnored(event.target, ignoreSelector)) {
            return;
        }

        record();
    }

    function handleKeyDown(event) {
        if (
            !meaningfulKeys.has(event.key) ||
            event.ctrlKey ||
            event.metaKey ||
            event.altKey
        ) {
            return;
        }

        if (
            document.activeElement &&
            interactionIsIgnored(document.activeElement, ignoreSelector)
        ) {
            return;
        }

        record();
    }

    function handleScroll() {
        if (Math.abs(window.scrollY - initialScrollY) >= scrollThreshold) {
            record();
        }
    }

    document.addEventListener('pointerdown', handlePointerDown, {
        passive: true
    });

    document.addEventListener('keydown', handleKeyDown);

    window.addEventListener('scroll', handleScroll, {
        passive: true
    });

    return cleanup;
}

function formatType(type) {
    const value = String(type);

    const knownTypes = {
        elearning: 'eLearning',
        e_learning: 'eLearning'
    };

    if (knownTypes[value.toLowerCase()]) {
        return knownTypes[value.toLowerCase()];
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatViewedAt(timestamp) {
    const elapsedMs = Date.now() - timestamp;
    const locale = document.documentElement.lang || undefined;

    try {
        const relative = new Intl.RelativeTimeFormat(locale, {
            numeric: 'auto'
        });

        const minute = 60 * 1000;
        const hour = 60 * minute;
        const day = 24 * hour;

        if (elapsedMs < minute) {
            return relative.format(0, 'second');
        }

        if (elapsedMs < hour) {
            return relative.format(
                -Math.max(1, Math.floor(elapsedMs / minute)),
                'minute'
            );
        }

        if (elapsedMs < day) {
            return relative.format(
                -Math.max(1, Math.floor(elapsedMs / hour)),
                'hour'
            );
        }

        if (elapsedMs < 7 * day) {
            return relative.format(
                -Math.max(1, Math.floor(elapsedMs / day)),
                'day'
            );
        }
    } catch {
        // Fall through to absolute date formatting.
    }

    try {
        return new Intl.DateTimeFormat(locale, {
            day: 'numeric',
            month: 'short'
        }).format(new Date(timestamp));
    } catch {
        return new Date(timestamp).toLocaleDateString();
    }
}

function createHistoryPanel() {
    if (panel) {
        return panel;
    }

    panel = document.createElement('div');
    panel.id = PANEL_ID;
    panel.className = 'recently-viewed-panel';
    panel.hidden = true;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');

    document.body.appendChild(panel);

    return panel;
}

function renderHistory() {
    createHistoryPanel();

    const allHistory = readHistory();
    const history = allHistory.slice(0, visibleHistoryItems);
    panel.setAttribute('aria-label', translations.history_heading);
    panel.replaceChildren();

    const header = document.createElement('div');
    header.className = 'recently-viewed-header';

    const heading = document.createElement('strong');
    heading.className = 'recently-viewed-heading';
    heading.textContent = translations.history_heading;

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'recently-viewed-close';
    closeButton.setAttribute('aria-label', translations.history_close);
    closeButton.textContent = '\u00d7';
    closeButton.addEventListener('click', () => {
        closeHistory(true);
    });

    header.append(heading, closeButton);
    panel.appendChild(header);

    if (!history.length) {
        const empty = document.createElement('p');
        empty.className = 'recently-viewed-empty';
        empty.textContent = translations.history_empty;
        panel.appendChild(empty);

        return;
    }

    const list = document.createElement('ul');
    list.className = 'recently-viewed-list';

    for (const item of history) {
        const listItem = document.createElement('li');
        listItem.className = 'recently-viewed-list-item';

        const link = document.createElement('a');
        link.href = item.uri;
        link.className = 'recently-viewed-item';

        const type = document.createElement('span');
        type.className = 'recently-viewed-type';
        type.textContent = formatType(item.type);

        const title = document.createElement('span');
        title.className = 'recently-viewed-title';
        title.textContent = item.title;

        const viewedAt = document.createElement('span');
        viewedAt.className = 'recently-viewed-date';
        viewedAt.textContent = formatViewedAt(item.viewedAt);

        link.append(type, title, viewedAt);

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'recently-viewed-remove';
        removeButton.setAttribute(
            'aria-label',
            `Remove ${item.title} from recently viewed`
        );
        removeButton.textContent = '\u00d7';

        removeButton.addEventListener('click', event => {
            event.preventDefault();
            event.stopPropagation();

            removeHistoryItem(item.id, item.type);
        });

        listItem.append(link, removeButton);
        list.appendChild(listItem);
    }

    panel.appendChild(list);

    const footer = document.createElement('div');
    footer.className = 'recently-viewed-footer';

    if (visibleHistoryItems < allHistory.length) {
        const showMoreButton = document.createElement('button');
        showMoreButton.type = 'button';
        showMoreButton.className = 'recently-viewed-show-more';
        showMoreButton.textContent = translations.history_show_more;

        showMoreButton.addEventListener('click', event => {
            event.preventDefault();
            event.stopPropagation();
            visibleHistoryItems += HISTORY_PAGE_SIZE;
            renderHistory();
            positionHistoryPanel();
        });

        footer.appendChild(showMoreButton);
    }

    const clearButton = document.createElement('button');
    clearButton.type = 'button';
    clearButton.className = 'recently-viewed-clear';
    clearButton.textContent = translations.history_clear;
    clearButton.addEventListener('click', clearHistory);

    footer.appendChild(clearButton);
    panel.appendChild(footer);
}

function positionHistoryPanel() {
    if (!panel || !activeButton) {
        return;
    }

    const anchor =
        activeButton.closest('.search-inline-control') || activeButton;

    const rect = anchor.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const edgeGap = 8;

    panel.style.position = 'fixed';
    panel.style.top = `${rect.bottom + edgeGap}px`;

    panel.style.right =
        `${Math.max(edgeGap, viewportWidth - rect.right)}px`;

    panel.style.left = 'auto';
}

function openHistory(button) {
    activeButton = button;
    visibleHistoryItems = HISTORY_PAGE_SIZE;

    renderHistory();

    panel.hidden = false;
    positionHistoryPanel();

    for (const historyButton of document.querySelectorAll(buttonSelector)) {
        historyButton.setAttribute(
            'aria-expanded',
            historyButton === activeButton ? 'true' : 'false'
        );
    }
}

function closeHistory(returnFocus = false) {
    if (!panel || panel.hidden) {
        return;
    }

    panel.hidden = true;

    for (const historyButton of document.querySelectorAll(buttonSelector)) {
        historyButton.setAttribute('aria-expanded', 'false');
    }

    if (returnFocus && activeButton) {
        activeButton.focus();
    }

    activeButton = null;
}

function handleHistoryButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;

    if (
        panel &&
        !panel.hidden &&
        activeButton === button
    ) {
        closeHistory();
        return;
    }

    openHistory(button);
}

/**
 * Initialise the history dropdown/panel UI.
 *
 * Safe to call more than once.
 *
 * Options:
 *   buttonSelector: selector for the history button(s).
 */
export function initHistory(options = {}) {
    if (historyUiInitialised) {
        return;
    }

    buttonSelector = options.buttonSelector || DEFAULT_BUTTON_SELECTOR;

    const buttons = document.querySelectorAll(buttonSelector);

    if (!buttons.length) {
        return;
    }

    updateHistoryButtonCount();

    historyUiInitialised = true;

    createHistoryPanel();

    for (const button of buttons) {
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', PANEL_ID);
        button.addEventListener('click', handleHistoryButtonClick);
    }

    document.addEventListener('click', event => {
        if (
            panel &&
            !panel.hidden &&
            !panel.contains(event.target) &&
            !event.target.closest(buttonSelector)
        ) {
            closeHistory();
        }
    });

    document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && panel && !panel.hidden) {
            closeHistory(true);
        }
    });

    window.addEventListener('resize', () => {
        if (panel && !panel.hidden) {
            positionHistoryPanel();
        }
    });

    window.addEventListener('scroll', () => {
        if (panel && !panel.hidden) {
            positionHistoryPanel();
        }
    }, {
        passive: true
    });

    window.addEventListener('storage', event => {
        if (
            event.key === STORAGE_KEY &&
            panel &&
            !panel.hidden
        ) {
            renderHistory();
            positionHistoryPanel();
        }
    });

    document.addEventListener('ecancer:historychange', () => {
        updateHistoryButtonCount();
        if (panel && !panel.hidden) {
            renderHistory();
            positionHistoryPanel();
        }
    });

    // Update the panel if another browser tab changes localStorage
    window.addEventListener('storage', event => {
        if (event.key === STORAGE_KEY) {
            updateHistoryButtonCount();

            if (panel && !panel.hidden) {
                renderHistory();
                positionHistoryPanel();
            }
        }
    });}